import { IconButton, SxProps, Theme, useTheme } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { memo } from 'react';
import { DynamicIcon, EllipsesMenu, EntityOpenModeSwitcher, Tooltip } from 'src/components';
import { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { paneHeaderIconButtonSx } from 'src/components/Navbar/navbar.styles';
import { ViewerListStateful } from 'src/components/ViewerListStateful/viewer-list-stateful';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  Address,
  Conversation,
  useResolvedAddress,
  useViewRecordFolders,
  VIEW_ASSIGNMENT_ENTITIES,
} from 'src/graphql';
import {
  useCreateRecordFolderAssignment,
  useDeleteRecordFolderAssignment,
} from 'src/graphql/entities/record-folder-assignment';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { useConversationActions } from 'src/pages/Conversations/use-conversation-actions';
import { useConversationQuickActions } from 'src/pages/Conversations/use-conversation-quick-actions';
import { useSiteQuickActions } from 'src/pages/Sites/use-site-quick-actions';
import { useGlobalStore } from 'src/store';
import { TabType, useGlobalTabs } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

const OPEN_DETAILS_PANEL_TOOLTIP = 'Open Conversation Details';
const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Conversation Details';

const mobileDetailsToggleSx: SxProps<Theme> = {
  ...paneHeaderIconButtonSx,
  width: '2rem',
  height: '2rem',
  marginLeft: 'auto',
};

interface ConversationWindowHeaderProps {
  conversation: Conversation;
  isConversationDetailsOpen: boolean;
  onConversationDetailsToggle: (isOpen: boolean) => void;
  isConversationExpanded?: boolean;
  customViewId?: string | null;
  handleAfterDelete?: () => void;
}

const SiteQuickActions = ({
  address,
  conversation,
}: {
  address: Address;
  conversation?: Conversation;
}) => {
  const { resolvedAddress, loading } = useResolvedAddress(address);

  // useSiteQuickActions consists of hooks that is hard to skip conditionally
  useSiteQuickActions(resolvedAddress ?? address, conversation, {
    disable: loading || !resolvedAddress,
  });
  return null;
};

const ConversationWindowHeaderComponent = ({
  conversation,
  isConversationDetailsOpen,
  onConversationDetailsToggle,
  isConversationExpanded,
  customViewId,
  handleAfterDelete,
}: ConversationWindowHeaderProps) => {
  const { isMobile } = useScreenDimensions();
  const theme = useTheme();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { manageableRecordFolders: recordFolders } = useViewRecordFolders();
  const { deleteRecordFolderAssignment } = useDeleteRecordFolderAssignment();
  const { createRecordFolderAssignment } = useCreateRecordFolderAssignment({
    entityFilter: VIEW_ASSIGNMENT_ENTITIES,
  });
  const { closeTabWith } = useGlobalTabs();
  const { navigate } = useNavigateRoute();

  const closeTab = () => {
    closeTabWith(
      (tab) => tab.type === TabType.Conversation && tab.entity?.id === conversation.id,
      false,
    );
  };

  useConversationQuickActions({ conversation, handleAfterDelete, customViewId });

  const {
    copyConversationId,
    copyFriendlyIdAsLink,
    copyUrl,
    copyConversationAsMarkdown,
    downloadConversationAsPdf,
    copyUuid,
    copyTOS,
    mergeConversation,
    deleteConversation,
    showActivityFeed,
  } = useConversationActions({
    conversation,
    handleAfterDelete,
    customViewId,
  });

  const menuOptions = [
    copyConversationId,
    copyFriendlyIdAsLink,
    copyUuid,
    copyUrl,
    copyTOS,
    {
      ...copyConversationAsMarkdown,
      isAllowed: copyConversationAsMarkdown.isAllowed && !isMobile,
    },
    {
      ...downloadConversationAsPdf,
      isAllowed: downloadConversationAsPdf.isAllowed && !isMobile,
    },
    showActivityFeed,
    mergeConversation,
    {
      label: 'Add to folder...',
      icon: 'folder_supervised',
      isAllowed: !!recordFolders?.length,
      handler: () => null,
      subItems: recordFolders.map((i) => {
        const assignment = i.assignments?.find((i) => i.entityId === conversation.friendlyId);

        return {
          id: i.id,
          label: i.name,
          icon: 'folder',
          iconColor: assignment ? theme.palette.custom.OV.sys.secondary : undefined,
          selected: Boolean(assignment),
          onClick: () => {
            if (assignment) {
              void deleteRecordFolderAssignment(assignment);
            } else {
              void createRecordFolderAssignment({
                recordFolderId: i.id,
                entity: 'Conversation',
                entityId: conversation.friendlyId,
                partnerId: selectedPartnerId ?? PLATFORM_PARTNER_ID,
                sortOrder: 0,
                title: conversation.friendlyId,
              });
            }
          },
        };
      }),
    },
    deleteConversation,
  ].filter((option) => ('isAllowed' in option ? option.isAllowed : true));
  const menuHasLoadingState = menuOptions.some(
    (option) => 'isLoading' in option && Boolean(option.isLoading),
  );

  const tooltipTitle = isConversationDetailsOpen
    ? CLOSE_DETAILS_PANEL_TOOLTIP
    : OPEN_DETAILS_PANEL_TOOLTIP;

  const detailsToggleIconName = isConversationDetailsOpen
    ? 'right_panel_close'
    : 'right_panel_open';
  const toggleDetails = () => onConversationDetailsToggle(!isConversationDetailsOpen);

  return (
    <>
      <ViewerListStateful
        entity="Conversation"
        entityId={conversation?.id}
        isHidden={isMobile}
      />
      {!!conversation.address && (
        <SiteQuickActions address={conversation.address} conversation={conversation} />
      )}
      {!isMobile && (
        <EntityOpenModeSwitcher
          openMode={isConversationExpanded ? RecordOpenMode.SidePeek : RecordOpenMode.FullPage}
          onClick={() => {
            if (isConversationExpanded) {
              closeTab();
              navigate(
                portalPaths.entity.all({
                  entityName: 'conversations',
                  id: conversation.friendlyId,
                  customViewId,
                }),
              );
            } else {
              navigate(portalPaths.conversation.full(conversation.friendlyId), {
                state: {
                  customViewId,
                },
              });
            }
          }}
        />
      )}

      {menuOptions.length > 0 ? (
        <EllipsesMenu
          isLoading={menuHasLoadingState}
          triggerTestId="conversation-menu-trigger"
          options={menuOptions.map((action) => ({
            label: action.label,
            testId: 'testId' in action ? action.testId : undefined,
            icon: action.icon,
            iconLibrary: 'iconLibrary' in action ? action.iconLibrary : undefined,
            onClick: action.handler,
            ...('subItems' in action
              ? { subItems: action.subItems as EllipsesMenuOption[] }
              : {}),
          }))}
        />
      ) : null}

      <Tooltip title={tooltipTitle}>
        {isMobile ? (
          <IconButton
            disableRipple
            sx={mobileDetailsToggleSx}
            onClick={toggleDetails}
            data-testid="conversation-details-toggle"
          >
            <DynamicIcon name={detailsToggleIconName} />
          </IconButton>
        ) : (
          <DynamicIcon
            name={detailsToggleIconName}
            sx={{ cursor: 'pointer' }}
            onClick={toggleDetails}
          />
        )}
      </Tooltip>
    </>
  );
};

export const ConversationWindowHeader = memo(ConversationWindowHeaderComponent);
